import {useCallback, useEffect, useRef} from 'react'

type Debounced<Args extends any[]> = (...args: Args) => void

/**
 * Возвращает функцию, которая вызовет переданный колбэк,
 * только если прошло `waitMs` миллисекунд с момента последнего вызова.
 * Хук гарантирует, что колбэк не будет вызван после анмаунта компонента.
 *
 * @param callback Колбэк
 * @param waitMs Время ожидания вызова колбэка
 * @typeParam Args Типы аргументов колбэка
 * @returns Функция, вызывающая колбэк только спустя `waitMs` миллисекунд
 *          с момента последнего вызова полученной функции
 */
export function useDebouncedFunction<Args extends any[]>(
  callback: (...args: Args) => unknown,
  waitMs: number,
): Debounced<Args> {
  const timer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [])

  return useCallback(
    (...args: Args) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
      timer.current = setTimeout(() => {
        callback(...args)
      }, waitMs)
    },
    [callback, waitMs],
  )
}
